import React from "react";
import { Download, ExternalLink } from "lucide-react";
import { AppButton, LinkedInIcon, cn } from "../../ui";

/** LinkedIn's "Add licence or certification" form, pre-filled for a participation certificate. */
export const linkedInAddUrl = (cert) => {
  const issued = new Date(cert.issuedAt || Date.now());
  const qs = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: `Certificate of Participation — ${cert.eventTitle}`,
    organizationName: cert.instituteName || "",
    issueYear: String(issued.getFullYear()),
    issueMonth: String(issued.getMonth() + 1),
    certUrl: cert.verifyUrl || "",
    certId: cert.certificateNo,
  });
  return `https://www.linkedin.com/profile/add?${qs.toString()}`;
};

/**
 * CertificateShareActions — what a certificate holder does with it: download
 * the PDF, open its public verification page, add it to LinkedIn.
 *
 * Used on the public verification page (anyone, a public download link) and on
 * the attendee's own post-session page (their credential, a short-lived link
 * fetched on click) — hence `downloadHref` OR `onDownload`.
 *
 * Props:
 *   cert         — the certificate's public view ({ certificateNo, eventTitle, verifyUrl, … })
 *   downloadHref — a plain link to the PDF
 *   onDownload   — or a handler that fetches one
 *   downloading  — busy state for `onDownload`
 *   showVerify   — include "View & verify" (pointless on the verification page itself)
 *   align        — "start" | "center"
 */
const CertificateShareActions = ({
  cert, downloadHref, onDownload, downloading = false, showVerify = false, align = "start",
}) => (
  <div className={cn("tw-flex tw-flex-wrap tw-gap-2", align === "center" && "tw-justify-center")}>
    {downloadHref ? (
      <AppButton size="sm" as="a" href={downloadHref}>
        <Download size={16} /> Download PDF
      </AppButton>
    ) : (
      <AppButton size="sm" onClick={onDownload} disabled={downloading}>
        <Download size={16} /> {downloading ? "Preparing…" : "Download PDF"}
      </AppButton>
    )}
    {showVerify && cert?.verifyUrl && (
      <AppButton size="sm" variant="outline" as="a" href={cert.verifyUrl} target="_blank" rel="noopener noreferrer">
        <ExternalLink size={16} /> View &amp; verify
      </AppButton>
    )}
    {cert?.eventTitle && (
      <AppButton size="sm" variant="outline" as="a" href={linkedInAddUrl(cert)} target="_blank" rel="noopener noreferrer">
        <LinkedInIcon size={16} /> Add to LinkedIn
      </AppButton>
    )}
  </div>
);

export default CertificateShareActions;
